// src/components/auth/SignInForm.tsx
"use client";
import Checkbox from "@/components/form/input/Checkbox";
import Input from "@/components/form/input/InputField";
import Label from "@/components/form/Label";
import Button from "@/components/ui/button/Button";
import { ChevronLeftIcon, EyeCloseIcon, EyeIcon } from "@/icons";
import Link from "next/link";
import React, { useState } from "react";
import { useAuth } from "@/hooks/auth/useAuth";
import { useAuthForm } from "@/hooks/auth/useAuthForm";
import { useAlertContext } from "@/providers/AlertProvider";
import { useRouter } from "next/navigation";
import { ROUTES, getEntryPath } from "@/config/routes";

interface SignInFormProps {
  /** 'platform' only accepts SuperAdmin logins; 'business' (default) accepts everyone else.
   * Keeps SuperAdmin and Business as genuinely separate sign-in entry points instead of one
   * shared form redirecting by role after the fact. */
  expectedTier?: 'business' | 'platform';
}

export default function SignInForm({ expectedTier = 'business' }: SignInFormProps) {
  const router = useRouter();
  const { login, logout, isLoading } = useAuth();
  const { 
    errors, 
    validateLoginForm, 
    clearErrors, 
    validateEmail,
    setLoading 
  } = useAuthForm();
  
  const { error: showError, success: showSuccess } = useAlertContext();
  
  const [showPassword, setShowPassword] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field when user starts typing
    if (errors[name as keyof typeof errors]) {
      clearErrors();
    }
  };

 // Update the redirect logic in handleSubmit function
const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  clearErrors();

  if (!validateLoginForm({ email: formData.email, password: formData.password })) {
    return;
  }

  setLoading(true);

  try {
    const user = await login({
      email: formData.email,
      password: formData.password
    });

    // Was `user.roles.includes('admin')` — written when 'admin' was the only super-user role
    // and "platform" tier just meant "signed in as admin". The SaaS transformation added a
    // real, distinct platform_admin role/claim (reserved platform tenant + platform_admin role
    // together — see JwtTokenService.IssuePairAsync); left unchanged, this would reject every
    // genuine platform admin ("isn't a platform administrator") while welcoming any store's own
    // "admin" in as if they were one — exactly backwards. Live-caught the moment a real
    // platform_admin account tried to sign in here.
    const isPlatformTier = !!user.isPlatformAdmin;

    // Reject cross-tier logins outright rather than silently redirecting — a platform
    // account has no business dashboard to land on, and vice versa. This is what makes
    // /signin and /platform/signin genuinely separate entry points instead of one shared
    // form that happens to redirect differently afterwards.
    if (expectedTier === 'platform' && !isPlatformTier) {
      logout();
      showError("Wrong sign-in", "This account isn't a platform administrator. Use the business sign-in instead.");
      setLoading(false);
      return;
    }
    if (expectedTier === 'business' && isPlatformTier) {
      logout();
      showError("Wrong sign-in", "Platform administrators sign in at /platform/signin.");
      setLoading(false);
      return;
    }

    showSuccess("Success", "Login successful!");

    if (isChecked) {
      localStorage.setItem("rememberMe", "true");
    } else {
      localStorage.removeItem("rememberMe");
    }

    // IMPORTANT: Wait a moment for cookies to be set
    await new Promise(resolve => setTimeout(resolve, 100));

    const primaryRole = user.roles.includes('admin')
      ? 'admin'
      : user.roles.includes('manager')
        ? 'manager'
        : (user.roles[0] ?? 'customer');

    // Use window.location instead of router for full page reload
    window.location.href = getEntryPath(primaryRole);

  } catch (error) {
    console.error("Login error:", error);
    showError("Error", "Login failed. Please try again.");
  } finally {
    setLoading(false);
  }
};

  // Handle social login
  const handleSocialLogin = (provider: string) => {
    showError("Coming Soon", `${provider} login will be available soon!`);
  };

  // Pre-fill email from localStorage if remember me was checked
  React.useEffect(() => {
    const rememberMe = localStorage.getItem("rememberMe");
    if (rememberMe === "true") {
      setIsChecked(true);
    }
  }, []);

  return (
    <div className="flex flex-col flex-1 lg:w-1/2 w-full">
      <div className="w-full max-w-md sm:pt-10 mx-auto mb-5">
        <Link
          href={ROUTES.LANDING.path}
          className="inline-flex items-center text-sm text-gray-500 transition-colors hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
        >
          <ChevronLeftIcon />
          Back to home
        </Link>
      </div>
      <div className="flex flex-col justify-center flex-1 w-full max-w-md mx-auto">
        <div>
          <div className="mb-5 sm:mb-8">
            <h1 className="mb-2 font-semibold text-gray-800 text-title-sm dark:text-white/90 sm:text-title-md">
              {expectedTier === 'platform' ? 'Platform Sign In' : 'Sign In'}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {expectedTier === 'platform'
                ? 'Platform administrator access only.'
                : 'Enter your email and password to sign in!'}
            </p>
          </div>

          <div>
            {/* Social login buttons — business sign-in only; platform admin accounts aren't self-registered */}
            {expectedTier === 'business' && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-5">
              <button 
                type="button"
                onClick={() => handleSocialLogin("Google")}
                className="inline-flex items-center justify-center gap-3 py-3 text-sm font-normal text-gray-700 transition-colors bg-gray-100 rounded-lg px-7 hover:bg-gray-200 hover:text-gray-800 dark:bg-white/5 dark:text-white/90 dark:hover:bg-white/10"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18.7511 10.1944C18.7511 9.47495 18.6915 8.94995 18.5626 8.40552H10.1797V11.6527H15.1003C15.0011 12.4597 14.4654 13.675 13.2749 14.4916L13.2582 14.6003L15.9087 16.6126L16.0924 16.6305C17.7788 15.1041 18.7511 12.8583 18.7511 10.1944Z"
                    fill="#4285F4"
                  />
                  <path
                    d="M10.1788 18.75C12.5895 18.75 14.6133 17.9722 16.0915 16.6305L13.274 14.4916C12.5201 15.0068 11.5081 15.3666 10.1788 15.3666C7.81773 15.3666 5.81379 13.8402 5.09944 11.7305L4.99473 11.7392L2.23868 13.8295L2.20264 13.9277C3.67087 16.786 6.68674 18.75 10.1788 18.75Z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.10014 11.7305C4.91165 11.186 4.80257 10.6027 4.80257 9.99992C4.80257 9.3971 4.91165 8.81379 5.09022 8.26935L5.08523 8.1534L2.29464 6.02954L2.20333 6.0721C1.5982 7.25823 1.25098 8.5902 1.25098 9.99992C1.25098 11.4096 1.5982 12.7415 2.20333 13.9277L5.10014 11.7305Z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M10.1789 4.63331C11.8554 4.63331 12.9864 5.34303 13.6312 5.93612L16.1511 3.525C14.6035 2.11528 12.5895 1.25 10.1789 1.25C6.68676 1.25 3.67088 3.21387 2.20264 6.07218L5.08953 8.26943C5.81381 6.15972 7.81776 4.63331 10.1789 4.63331Z"
                    fill="#EB4335"
                  />
                </svg>
                Sign in with Google
              </button>
              <button 
                type="button"
                onClick={() => handleSocialLogin("X")}
                className="inline-flex items-center justify-center gap-3 py-3 text-sm font-normal text-gray-700 transition-colors bg-gray-100 rounded-lg px-7 hover:bg-gray-200 hover:text-gray-800 dark:bg-white/5 dark:text-white/90 dark:hover:bg-white/10"
              >
                <svg
                  width="21"
                  className="fill-current"
                  height="20"
                  viewBox="0 0 21 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M15.6705 1.875H18.4272L12.4047 8.75833L19.4897 18.125H13.9422L9.59717 12.4442L4.62554 18.125H1.86721L8.30887 10.7625L1.51221 1.875H7.20054L11.128 7.0675L15.6705 1.875ZM14.703 16.475H16.2305L6.37054 3.43833H4.73137L14.703 16.475Z" />
                </svg>
                Sign in with X
              </button>
            </div>
            )}

            {expectedTier === 'business' && (
            <div className="relative py-3 sm:py-5">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200 dark:border-gray-800"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="p-2 text-gray-400 bg-white dark:bg-gray-900 sm:px-5 sm:py-2">
                  Or
                </span>
              </div>
            </div>
            )}

            {/* Login Form */}
            <form onSubmit={handleSubmit}>
              <div className="space-y-6">
                {/* Email Field */}
                <div>
                  <Label htmlFor="email">
                    Email <span className="text-error-500">*</span>
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="info@gmail.com"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={errors.email ? "border-error-500" : ""}
                    required
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-error-500">{errors.email}</p>
                  )}
                </div>
                
                {/* Password Field */}
                <div>
                  <Label htmlFor="password">
                    Password <span className="text-error-500">*</span>
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className={errors.password ? "border-error-500" : ""}
                      required
                    />
                    <span
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute z-30 -translate-y-1/2 cursor-pointer right-4 top-1/2"
                    >
                      {showPassword ? (
                        <EyeIcon className="fill-gray-500 dark:fill-gray-400" />
                      ) : (
                        <EyeCloseIcon className="fill-gray-500 dark:fill-gray-400" />
                      )}
                    </span>
                  </div>
                  {errors.password && (
                    <p className="mt-1 text-sm text-error-500">{errors.password}</p>
                  )}
                </div>
                
                {/* Remember Me & Forgot Password */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Checkbox 
                      checked={isChecked} 
                      onChange={setIsChecked}
                      id="rememberMe"
                    />
                    <label 
                      htmlFor="rememberMe"
                      className="block font-normal text-gray-700 cursor-pointer text-theme-sm dark:text-gray-400"
                    >
                      Keep me logged in
                    </label>
                  </div>
                  <Link
                    href={ROUTES.FORGOT_PASSWORD.path}
                    className="text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400"
                  >
                    Forgot password?
                  </Link>
                </div>
                
                {/* Submit Button */}
                <div>
                  <Button 
                    type="submit"
                    className="w-full" 
                    size="sm"
                    disabled={isLoading}
                  >
                    {isLoading ? "Signing in..." : "Sign in"}
                  </Button>
                </div>
              </div>
            </form>

            {/* Sign Up Link — platform admin accounts aren't self-registered */}
            {expectedTier === 'business' && (
            <div className="mt-5">
              <p className="text-sm font-normal text-center text-gray-700 dark:text-gray-400 sm:text-start">
                Don&apos;t have an account? {""}
                <Link
                  href={ROUTES.SIGNUP.path}
                  className="text-brand-500 hover:text-brand-600 dark:text-brand-400"
                >
                  Sign Up
                </Link>
              </p>
            </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}